import { map, switchMap, zip } from 'rxjs/operators';
import { Component, OnInit, ChangeDetectorRef, ViewChild, OnDestroy } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';
import { Location } from '@angular/common';
import {
  PeopleService,
  LanguageService,
  ApplicationService,
  MachineFamillyService,
  LocalEntityService
} from '../../shared/services/index';
import { BaseModalPageComponent } from '../../base-modal.component';
import { ModalComponent } from '../../modal/modal.component';
import { ConfirmComponent } from '../../modal/confirm.component';
import { ProfilPipe, LocalEntityPipe } from '../../pipes/index';
import * as _ from 'lodash';
import { Person } from '../../shared/models';
import { PeopleMenuComponent } from '../../menus/plugins/people.menu';
import { PermissionService } from '../../shared/services/permission.service';
import { Subscription } from 'rxjs';

@Component({
    templateUrl: './bulkedit.component.html',
    standalone: false
})
export class BulkEditPersonDetailPageComponent extends BaseModalPageComponent implements OnInit, OnDestroy {
  public people: Person[];
  public personId: string;
  editForm: UntypedFormGroup;
  public processing = false;
  public languages: any[];
  public applicationTypes: any[];
  @ViewChild('menuModal', { static: true })
  menuModal: ModalComponent;
  @ViewChild('deleteConfirm', { static: true })
  deleteConfirm: ConfirmComponent;
  @ViewChild('permissionsModal', { static: true })
  permissionsModal: ModalComponent;
  @ViewChild('productLinesModal', { static: true })
  productLinesModal: ModalComponent;
  @ViewChild('localEntitiesModal', { static: true })
  localEntitiesModal: ModalComponent;
  @ViewChild('officeModal', { static: true })
  officeModal: ModalComponent;
  public permissions: any;
  public productLines: any[];
  public localEntities: any;
  public loading = false;

  private sub = new Subscription();

  constructor(
    location: Location,
    private language: LanguageService,
    private peopleService: PeopleService,
    private applicationService: ApplicationService,
    private profilPipe: ProfilPipe,
    private localEntityPipe: LocalEntityPipe,
    private cdf: ChangeDetectorRef,
    private machineFamillyService: MachineFamillyService,
    private localEntityService: LocalEntityService,
    private permissionService: PermissionService,
    private fb: UntypedFormBuilder
  ) {
    super(location);
  }
  ngOnDestroy(): void {
    this.peopleService.setPeopleToEdit([]);
    this.sub.unsubscribe();
  }

  ngOnInit(): void {
    const self = this;
    this.loading = true;
    this.sub.add(
      self.localEntityService
        .getLocalEntities()
        .pipe(zip(this.applicationService.getApplicationTypes()))
        .pipe(
          switchMap(([localEntities, types]) => {
            self.localEntities = _.orderBy(localEntities, ['label']);
            self.applicationTypes = types;
            return self.initRoute();
          }),
          switchMap(() => {
            return self.language.getLanguageList().pipe(map((data) => (this.languages = data)));
          })
        )
        .subscribe(() => {
          this.loading = false;
          this.cdf.detectChanges();
        })
    );
  }

  initRoute() {
    return this.initData();
  }

  public cancel() {
    this.goToBack();
  }

  save(): void {
    this.processing = true;

    const peopleToEdit = _.map(this.people, (t: any) => t.id);

    let personData = {
      language:
        this.editForm.controls['updateLanguage']?.value === true ? this.editForm.controls['language']?.value : null,
      applicationTypes:
        this.editForm.controls['updateApplications']?.value === true
          ? this.editForm.controls['applicationTypes']?.value
          : null,
      permissions:
        this.editForm.controls['updateProfiles']?.value === true ? this.editForm.controls['permissions']?.value : null,
      localEntities:
        this.editForm.controls['updateLocalEntities']?.value === true
          ? this.editForm.controls['localEntities']?.value
          : null,
      productLine:
        this.editForm.controls['updateProductLine']?.value === true
          ? this.editForm.controls['productLine']?.value
          : null
    };

    if (personData.permissions && personData.permissions.length > 0) {
      _.remove(personData.permissions, (t: any) => !t.selected);
      personData.permissions = _.map(personData.permissions, (t: any) => t.value);
    }
    if (personData.localEntities && personData.localEntities.length > 0) {
      _.remove(personData.localEntities, (t: any) => !t.selected);
      personData.localEntities = _.map(personData.localEntities, (t: any) => t.value);
    }
    if (personData.productLine && personData.productLine.length > 0) {
      _.remove(personData.productLine, (t: any) => !t.selected);
      personData.productLine = _.map(personData.productLine, (t: any) => t.id);
    }

    this.peopleService.updatePeople({ peopleToEdit, personData }).subscribe((people: Person[]) => {
      this.processing = false;
      this.people = people;
      this.goToBack();
    });
  }

  initData() {
    return this.permissionService.getPermissionsList().pipe(
      zip(this.machineFamillyService.getProductLines()),
      switchMap(([permissions, productLines]) => {
        this.permissions = _.orderBy(permissions, ['label']);

        productLines.push(this.machineFamillyService.getAllProductLineItem());
        this.productLines = _.map(productLines, (p) => ({
          id: 'productline-' + p.id,
          name: p.name,
          label: p.name
        }));
        this.productLines = _.orderBy(this.productLines, ['name']);

        return this.peopleService.getPeopleToEdit().pipe(
          map((people) => {
            this.people = people;
            this.initForm();
          })
        );
      })
    );
  }

  isInPersonApplicationTypes(applicationType: number) {
    return (
      this.editForm?.controls?.applicationTypes?.value &&
      this.editForm.controls.applicationTypes.value.indexOf(applicationType) !== -1
    );
  }

  updateApplicationTypes(applicationType: number) {
    if (!this.isInPersonApplicationTypes(applicationType)) {
      this.editForm.controls.applicationTypes.value.push(applicationType);
    } else {
      const i = this.editForm.controls.applicationTypes
        ? this.editForm.controls.applicationTypes.value.indexOf(applicationType)
        : -1;
      this.editForm.controls.applicationTypes.value.splice(i, 1);
    }
    this.cdf.detectChanges();
  }

  public onProductLinesMenuClose() {
    this.editForm.controls['productLine'].setValue(this.productLines);
    this.editForm.controls['productLine'].markAsTouched();
  }

  public onLocalEntitiesMenuClose() {
    this.editForm.controls['localEntities'].setValue(this.localEntities);
    this.editForm.controls['localEntities'].markAsTouched();
  }

  public onPermissionsMenuClose() {
    this.editForm.controls['permissions'].setValue(this.permissions);
    this.editForm.controls['permissions'].markAsTouched();
  }

  initForm() {
    this.editForm = this.fb.group({
      updateLanguage: new UntypedFormControl(false),
      updateApplications: new UntypedFormControl(false),
      updateProfiles: new UntypedFormControl(false),
      updateLocalEntities: new UntypedFormControl(false),
      updateProductLine: new UntypedFormControl(false),
      language: new UntypedFormControl(null),
      applicationTypes: new UntypedFormControl([]),
      permissions: new UntypedFormControl(null),
      productLine: new UntypedFormControl(null),
      localEntities: new UntypedFormControl(null)
    });
  }

  processPermissions(permissions: any): any {
    const clonedPermissions = _.cloneDeep(this.permissions);
    _.each(permissions, (el) => {
      const i = _.findIndex(clonedPermissions, ['value', el]);
      if (i !== -1) {
        clonedPermissions[i].selected = true;
      }
    });
    return clonedPermissions;
  }

  processLocalEntities(localEntities: any): any {
    const clonedLocalEntities = _.cloneDeep(this.localEntities);
    _.each(localEntities, (el) => {
      const i = _.findIndex(clonedLocalEntities, ['value', el]);
      if (i !== -1) {
        clonedLocalEntities[i].selected = true;
      }
    });
    return clonedLocalEntities;
  }

  public getSelectedValues(): string {
    const permissions = this.editForm.controls['permissions'].value;
    const defaultValue = 'Select a permission';
    let selectedValues = _.filter(permissions, (item: any) => item.selected);
    selectedValues = _.map(selectedValues, (item: any) => this.profilPipe.transform(item.value));
    return selectedValues.length > 0 ? selectedValues.join('<br/>') : defaultValue;
  }

  public getSelectedLocalEntitiesValues(): string {
    const localEntities = this.editForm.controls['localEntities'].value;
    const defaultValue = 'Select a local entity';
    let selectedValues = _.filter(localEntities, (item: any) => item.selected);
    selectedValues = _.map(selectedValues, (item: any) => this.localEntityPipe.transform(item.value));
    return selectedValues.length > 0 ? selectedValues.join('<br/>') : defaultValue;
  }

  processProductLines(productLines: any): string {
    const clonedProductLines = _.cloneDeep(this.productLines);
    _.each(productLines, (el) => {
      const i = _.findIndex(clonedProductLines, ['id', el]);
      if (i !== -1) {
        clonedProductLines[i].selected = true;
      }
    });
    return clonedProductLines;
  }

  public getSelectedProductLineValues(): string {
    const productLines = this.editForm.controls['productLine'].value;
    const defaultValue = 'Select a product line';
    let selectedValues = _.filter(productLines, (item: any) => item.selected);
    selectedValues = selectedValues.map((s) => s.name);
    return selectedValues.length > 0 ? selectedValues.join('<br/>') : defaultValue;
  }

  public openPermissionsSelector(): void {
    this.openMultipleSelector(this.permissionsModal, this.permissions);
  }

  public openLocalEntitiesSelector(): void {
    this.openMultipleSelector(this.localEntitiesModal, this.localEntities, true);
  }

  public openProductLinesSelector(): void {
    this.openMultipleSelector(this.productLinesModal, this.productLines);
  }

  public openMenu(): void {
    this.menuModal.modalFooter = false;
    this.menuModal.modalHeader = false;
    this.menuModal.modalMessage = false;
    this.menuModal.open(PeopleMenuComponent);
  }

  public selectMenu(item: any) {
    switch (item) {
      case 1:
        this.openDeleteConfirm();
        break;
      default:
    }
  }

  openDeleteConfirm() {
    this.deleteConfirm.open();
  }

  deletePeople() {
    const peopleToDelete = _.map(this.people, (p) => p.id);
    this.peopleService.deletePeople(peopleToDelete).subscribe((result: any) => {
      this.goToBack();
    });
  }
}
